import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { db } from "../firebase";
import { useFavoris } from "../hooks/useFavoris";
import { FiHeart } from "react-icons/fi";
import { collection, getDocs, query, where } from "firebase/firestore";
import { Helmet } from "react-helmet";
import AOS from "aos";
import "aos/dist/aos.css";

import MenuNav from "../components/Menunav";
import HeroSection from "../components/HeroSection";
import MobileMenu from "../components/MobileMenu";
import BrandCard from "../components/BrandCard";
import FAQSection from "../components/FAQSection";
import AnimatedCounters from "../components/AnimatedCounters";
import FeaturedVehiclesModal from "../components/FeaturedVehiclesModal";
import PiecesHighlightSection from "../components/PiecesHighlightSection";

function shuffleArray(array = []) {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
}

export default function Home() {
  const [vehicles, setVehicles] = useState([]);
  const [brands, setBrands] = useState([]);
  const [search, setSearch] = useState("");
  const [visibleCount, setVisibleCount] = useState(9);

  const navigate = useNavigate();
  const favorisSlugs = useFavoris();

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const q = query(
          collection(db, "vehicules_auto"),
          where("enVente", "==", true)
        );

        const snapshot = await getDocs(q);
        const data = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));

        setVehicles(data.slice(0, 27));
      } catch (err) {
        console.error("Erreur lors du chargement des véhicules :", err);
      }
    };

    const fetchBrands = async () => {
      try {
        const snapshot = await getDocs(collection(db, "marques"));
        const data = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));
        setBrands(data.slice(0, 8));
      } catch (err) {
        console.error("Erreur lors du chargement des marques :", err);
      }
    };

    fetchVehicles();
    fetchBrands();
  }, []);

  const randomizedVehicles = useMemo(() => {
    return shuffleArray(vehicles);
  }, [vehicles]);

  const displayedVehicles = useMemo(() => {
    return randomizedVehicles.slice(0, visibleCount);
  }, [randomizedVehicles, visibleCount]);

  const loadMore = () => {
    if (
      visibleCount + 9 >= randomizedVehicles.length &&
      randomizedVehicles.length >= 27
    ) {
      navigate("/catalogue");
    } else {
      setVisibleCount((prev) => prev + 9);
    }
  };

  const keywords = [
    "The bridge",
    "voiture occasion",
    "voiture neuve",
    "véhicule importé",
    "SUV",
    "utilitaire",
    "achat véhicule Afrique",
    ...brands.map((b) => b.nom).filter(Boolean),
  ].join(", ");

  return (
    <div className="font-sans bg-light">
      <Helmet>
        <title>
          Vente de Voitures d’Occasions et Neuves | TheBridgeOfWorld.com
        </title>
        <meta
          name="description"
          content="Achetez des véhicules d'occasions ou neufs avec The Bridge of World. Qualité, transparence et satisfaction. Livraison en Afrique – SUV, berlines, utilitaires, hybrides."
        />
        <meta name="keywords" content={keywords} />
        <meta name="author" content="The Bridge of World" />
        <meta
          property="og:title"
          content="Vente de Voitures d’Occasions et Neuves | TheBridgeOfWorld.com"
        />
        <meta
          property="og:description"
          content="Explorez les meilleurs véhicules neufs et d'occasions. Livraison en Afrique, qualité garantie, prix compétitifs."
        />
        <meta property="og:url" content="https://thebridgeofworld.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="The Bridge of World" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "The Bridge of World",
            url: "https://thebridgeofworld.com/",
            potentialAction: {
              "@type": "SearchAction",
              target:
                "https://thebridgeofworld.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          })}
        </script>
      </Helmet>

      <MenuNav />
      <MobileMenu />
      <HeroSection />

      <div className="container mt-n5 position-relative z-3">
        <div className="bg-white shadow rounded p-4">
          <div className="row g-3">
            <div className="col-md-10" data-aos="fade-up">
              <input
                type="text"
                className="form-control"
                placeholder="Recherche de véhicule..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    navigate(
                      `/marques?recherche=${encodeURIComponent(search)}`
                    );
                  }
                }}
              />
            </div>
            <div className="col-md-2" data-aos="fade-up">
              <button
                className="btn btn-primary w-100"
                onClick={() =>
                  navigate(
                    `/catalogue?recherche=${encodeURIComponent(search)}`
                  )
                }
              >
                Rechercher
              </button>
            </div>
          </div>
        </div>
      </div>

      <section className="py-5 bg-light">
        <div className="container">
          <h2 className="text-center fw-bold mb-4">Marques Populaires</h2>
          <div className="row g-4">
            {brands.map((m) => (
              <div key={m.id} className="col-6 col-md-3">
                <Link
                  to={`/Vehiclelisting/${m.nom}`}
                  className="text-decoration-none"
                >
                  <BrandCard nom={m.nom} />
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-4">
            <Link
              to="/marques"
              className="btn btn-bridge btn-lg px-4 py-2"
              style={{ minWidth: "200px" }}
            >
              Voir toutes les marques
            </Link>
          </div>
        </div>
      </section>

      <section className="py-5 bg-white">
        <div className="container">
          <h2 className="text-center fw-bold mb-5">Véhicules en Vedette</h2>

          <div className="row g-3 g-md-4">
            {displayedVehicles.map((v) => (
              <div key={v.id} className="col-6 col-md-6 col-lg-4">
                <div
                  className="position-relative rounded overflow-hidden shadow-sm vehicle-card h-100 bg-white"
                  style={{ cursor: "pointer" }}
                  onClick={() => v.slug && navigate(`/vehicules/${v.slug}`)}
                >
                  <button
                    type="button"
                    className="position-absolute top-0 end-0 m-2 bg-white border-0 rounded-circle shadow"
                    style={{
                      width: "38px",
                      height: "38px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 3,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    title={
                      favorisSlugs.includes(v.slug)
                        ? "Retirer des favoris"
                        : "Ajouter aux favoris"
                    }
                  >
                    <FiHeart
                      size={18}
                      color={favorisSlugs.includes(v.slug) ? "red" : "gray"}
                    />
                  </button>

                  <img
                    loading="lazy"
                    src={v.imageUrls?.[0] || "/assets/default.jpg"}
                    alt={`${v.marque} ${v.modele}`}
                    className="w-100 vehicle-image"
                  />

                  <div className="d-block d-md-none position-absolute top-0 start-0 m-2 px-2 py-1 rounded bg-dark bg-opacity-75 text-white fw-semibold small shadow-sm">
                    {v.marque} {v.modele}
                  </div>

                  <div className="vehicle-overlay d-none d-md-flex flex-column justify-content-end p-3">
                    <div className="text-white">
                      <h5 className="fw-bold mb-1">
                        {v.marque} {v.modele}
                      </h5>

                      <p className="mb-2">
                        {v.annee} • {v.ficheTechnique?.carburant} •{" "}
                        {v.ficheTechnique?.transmission}
                      </p>

                      <p className="h5 text-primary fw-bold mb-3">
                        {parseInt(v.prix, 10) > 0 ? (
                          <>
                            {parseInt(v.prix, 10).toLocaleString("fr-FR")}{" "}
                            <span
                              className="text-white fw-semibold"
                              style={{ fontSize: "0.9rem" }}
                            >
                              FCFA
                            </span>
                          </>
                        ) : (
                          <span style={{ color: "#F7941E" }}>
                            Prix à la demande
                          </span>
                        )}
                      </p>

                      <button
                        type="button"
                        className="btn btn-bridge btn-sm"
                      >
                        Voir détails
                      </button>
                    </div>
                  </div>

                  <div className="d-block d-md-none p-2">
                    <h6 className="fw-bold mb-1 small text-truncate">
                      {v.marque} {v.modele}
                    </h6>

                    <p
                      className="mb-1 text-muted"
                      style={{
                        fontSize: "0.78rem",
                        lineHeight: "1.3",
                        minHeight: "32px",
                      }}
                    >
                      {v.annee} • {v.ficheTechnique?.carburant || "—"} •{" "}
                      {v.ficheTechnique?.transmission || "—"}
                    </p>

                    <p
                      className="mb-0 fw-bold text-primary"
                      style={{ fontSize: "0.92rem" }}
                    >
                      {parseInt(v.prix, 10) > 0 ? (
                        <>
                          {parseInt(v.prix, 10).toLocaleString("fr-FR")}{" "}
                          <span
                            className="text-muted fw-semibold"
                            style={{ fontSize: "0.78rem" }}
                          >
                            FCFA
                          </span>
                        </>
                      ) : (
                        <span style={{ color: "#F7941E" }}>
                          Prix à la demande
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {visibleCount < randomizedVehicles.length && (
            <div className="text-center mt-4">
              <button className="btn btn-outline-primary" onClick={loadMore}>
                Charger plus de véhicules
              </button>
            </div>
          )}
        </div>

        <style>{`
          .vehicle-card {
            transition: transform 0.3s ease, box-shadow 0.3s ease;
          }

          .vehicle-card:hover {
            transform: translateY(-5px);
          }

          .vehicle-image {
            height: 190px;
            object-fit: cover;
          }

          @media (min-width: 768px) {
            .vehicle-image {
              height: 280px;
            }
          }

          .vehicle-overlay {
            position: absolute;
            inset: 0;
            background: linear-gradient(to top, rgba(0, 0, 0, 0.70), transparent 60%);
            opacity: 0;
            transition: opacity 0.4s ease;
          }

          .vehicle-card:hover .vehicle-overlay {
            opacity: 1;
          }
        `}</style>
      </section>

      <section className="py-5 bg-light border-top border-bottom">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6 mb-4 mb-md-0">
              <div
                id="aboutCarousel"
                className="carousel slide"
                data-bs-ride="carousel"
                data-bs-interval="3000"
                data-bs-pause="hover"
              >
                <div className="carousel-inner rounded shadow-sm overflow-hidden">
                  <div className="carousel-item active">
                    <img
                      src="/assets/about15.jpeg"
                      className="d-block w-100 about-slide-img"
                      alt="Présentation de l'entreprise"
                    />
                  </div>
                  <div className="carousel-item">
                    <img
                      src="/assets/about1.jpg"
                      className="d-block w-100 about-slide-img"
                      alt="Équipe THE BRIDGE"
                    />
                  </div>
                  <div className="carousel-item">
                    <img
                      src="/assets/about13.jpg"
                      className="d-block w-100 about-slide-img"
                      alt="Activités de l'entreprise"
                    />
                  </div>
                  <div className="carousel-item">
                    <img
                      src="/assets/about2.jpg"
                      className="d-block w-100 about-slide-img"
                      alt="Service automobile"
                    />
                  </div>
                  <div className="carousel-item">
                    <img
                      src="/assets/about3.jpg"
                      className="d-block w-100 about-slide-img"
                      alt="Véhicules disponibles"
                    />
                  </div>
                  <div className="carousel-item">
                    <img
                      src="/assets/about11.jpg"
                      className="d-block w-100 about-slide-img"
                      alt="Importation automobile"
                    />
                  </div>
                  <div className="carousel-item">
                    <img
                      src="/assets/about8.jpg"
                      className="d-block w-100 about-slide-img"
                      alt="Accompagnement client"
                    />
                  </div>
                  <div className="carousel-item">
                    <img
                      src="/assets/about6.jpg"
                      className="d-block w-100 about-slide-img"
                      alt="Showroom THE BRIDGE"
                    />
                  </div>
                </div>

                <button
                  className="carousel-control-prev"
                  type="button"
                  data-bs-target="#aboutCarousel"
                  data-bs-slide="prev"
                >
                  <span
                    className="carousel-control-prev-icon"
                    aria-hidden="true"
                  ></span>
                  <span className="visually-hidden">Précédent</span>
                </button>

                <button
                  className="carousel-control-next"
                  type="button"
                  data-bs-target="#aboutCarousel"
                  data-bs-slide="next"
                >
                  <span
                    className="carousel-control-next-icon"
                    aria-hidden="true"
                  ></span>
                  <span className="visually-hidden">Suivant</span>
                </button>
              </div>
            </div>

            <div className="col-md-6">
              <h2 className="fw-bold text-primary mb-3">
                À propos de THE BRIDGE
              </h2>
              <p className="text-muted">
                Votre passerelle vers des véhicules de qualité et un service
                exceptionnel.
              </p>
              <p className="text-muted">
                Chez <strong>THE BRIDGE</strong>, nous connectons les passionnés
                d’automobile aux meilleures offres du marché.
              </p>
              <p className="text-muted">
                Basés au Togo, nous proposons une sélection de véhicules neufs
                et d’occasions,{" "}
                <em>
                  ainsi que leurs pièces et accessoires, pour répondre à tous
                  vos besoins
                </em>
                .
              </p>

              <ul className="list-unstyled mt-4">
                <li className="mb-2">
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  Large choix de marques internationales
                </li>
                <li className="mb-2">
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  Accompagnement personnalisé
                </li>
                <li className="mb-2">
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  Qualité garantie et inspection rigoureuse
                </li>
                <li className="mb-2">
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  Prix compétitifs et transparents
                </li>
                <li className="mb-2">
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  Livraison rapide et assistance dédiée
                </li>
              </ul>

              <p className="text-muted">
                Vivez une expérience automobile sans stress ! Découvrez notre
                catalogue et trouvez le véhicule qui vous correspond.
                <em> Votre satisfaction, notre engagement !</em>
              </p>

              <Link to="/contact" className="btn btn-primary mt-4">
                En savoir plus
              </Link>
            </div>
          </div>
        </div>
      </section>
      <AnimatedCounters />
      <FAQSection />
      <PiecesHighlightSection />
      <section className="py-5 bg-light border-top">
        <div className="container">
          <h2 className="text-center fw-bold mb-4">
            🚗 Pourquoi choisir THE BRIDGE ?
          </h2>
          <div className="row text-center g-4">
            <div className="col-md-4" data-aos="fade-up">
              <i className="bi bi-award-fill text-primary fs-2"></i>
              <h6 className="fw-semibold mt-2">Qualité vérifiée</h6>
              <p className="text-muted small">
                Tous nos véhicules sont minutieusement inspectés et certifiés.
              </p>
            </div>

            <div className="col-md-4" data-aos="fade-up">
              <i className="bi bi-cash-coin text-success fs-2"></i>
              <h6 className="fw-semibold mt-2">Prix transparents</h6>
              <p className="text-muted small">
                Aucune surprise : des prix clairs, affichés, compétitifs.
              </p>
            </div>

            <div className="col-md-4" data-aos="fade-up">
              <i className="bi bi-person-heart text-danger fs-2"></i>
              <h6 className="fw-semibold mt-2">Satisfaction client</h6>
              <p className="text-muted small">
                Assistance personnalisée avant et après l’achat.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5 bg-white border-top">
        <div className="container">
          <h2 className="text-center fw-bold mb-4">Avis de nos clients</h2>
          <div className="row g-4">
            <div className="col-md-4" data-aos="fade-up">
              <div className="border p-3 rounded shadow-sm">
                <p className="fst-italic">
                  "Un service rapide et professionnel. Ma voiture était prête en
                  moins d’une semaine."
                </p>
                <h6 className="fw-bold mb-0">Kossi A.</h6>
                <small className="text-muted">Client à Lomé</small>
              </div>
            </div>

            <div className="col-md-4" data-aos="fade-up">
              <div className="border p-3 rounded shadow-sm">
                <p className="fst-italic">
                  "Excellent rapport qualité/prix. Je recommande THE BRIDGE à
                  tous mes proches."
                </p>
                <h6 className="fw-bold mb-0">Awa K.</h6>
                <small className="text-muted">Cliente à Cotonou</small>
              </div>
            </div>

            <div className="col-md-4" data-aos="fade-up">
              <div className="border p-3 rounded shadow-sm">
                <p className="fst-italic">
                  "Le site est clair, les véhicules bien présentés, et le
                  personnel très pro."
                </p>
                <h6 className="fw-bold mb-0">Francis M.</h6>
                <small className="text-muted">Client au Togo</small>
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer
        style={{ backgroundColor: "#0C1E47" }}
        className="text-white pt-5 pb-3 mt-5 border-top"
      >
        <div className="container">
          <div className="row gy-4">
            <div className="col-md-4">
              <div className="logo-wrapper">
                <img
                  src="/BRIDGE BLANC.png"
                  alt="THE BRIDGE logo"
                  width="180"
                  className="mb-3"
                />
              </div>
              <p style={{ color: "#F7931E", fontWeight: 500 }}>
                Let’s connect the world together
              </p>
              <p className="small text-white-50">
                THE BRIDGE est votre partenaire de confiance pour l’achat de
                véhicules fiables à travers le monde.
              </p>
            </div>

            <div className="col-md-4">
              <h6 className="text-uppercase fw-semibold mb-3">Navigation</h6>
              <ul className="list-unstyled">
                <li>
                  <Link to="/" className="text-white-50 text-decoration-none">
                    Accueil
                  </Link>
                </li>
                <li>
                  <Link
                    to="/catalogue"
                    className="text-white-50 text-decoration-none"
                  >
                    Nos véhicules
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="text-white-50 text-decoration-none"
                  >
                    Contact
                  </Link>
                </li>
                <li>
                  <Link
                    to="/mentions-legales"
                    className="text-white-50 text-decoration-none"
                  >
                    Mentions légales
                  </Link>
                </li>
              </ul>
            </div>

            <div className="col-md-4">
              <h6 className="text-uppercase fw-semibold mb-3">
                Nous contacter
              </h6>
              <p className="text-white-50 small mb-1">
                <i className="bi bi-geo-alt me-2"></i>Lomé, Togo
              </p>
              <p className="text-white-50 small mb-1">
                <i className="bi bi-envelope me-2"></i>
                contact@thebridgeofworld.com
              </p>
              <p className="text-white-50 small mb-0">
                <i className="bi bi-phone me-2"></i>
                +228 78 81 54 94 / +228 99 41 00 41 / +228 93 90 72 95
              </p>
            </div>
          </div>

          <div className="text-center mt-4 pt-3 border-top border-secondary text-white-50 small">
            © {new Date().getFullYear()} THE BRIDGE — Tous droits réservés
          </div>
        </div>
      </footer>
       <FeaturedVehiclesModal />
      <a
        href="https://wa.me/22878815494"
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float"
        aria-label="Contacter sur WhatsApp"
      >
        <i className="bi bi-whatsapp fs-3"></i>
      </a>
    </div>
  );
}
